import axios, { AxiosHeaders, InternalAxiosRequestConfig } from "axios";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");

function normalizeBearer(t: string) {
  return (t || "").replace(/^Bearer\s+/i, "").trim();
}

const STORES: Storage[] = [sessionStorage, localStorage];

function getFirst(keys: string[]): string {
  for (const store of STORES) {
    for (const k of keys) {
      try {
        const v = store.getItem(k);
        if (v && String(v).trim()) return String(v).trim();
      } catch {}
    }
  }
  return "";
}

function getRole(): string {
  return (getFirst(["role", "user_type"]) || "").toLowerCase();
}

function getPathFromUrl(url?: string): string {
  const u = String(url || "").trim();
  if (!u) return "";

  if (u.startsWith("http://") || u.startsWith("https://")) {
    try {
      return new URL(u).pathname.toLowerCase();
    } catch {
      return u.toLowerCase();
    }
  }

  return u.toLowerCase();
}

/**
 * Endpoints that must be PUBLIC and must NOT receive Authorization headers.
 * This prevents admin/doctor/student tokens from contaminating school registration
 * and other public flows.
 */
function isPublicEndpoint(url?: string): boolean {
  const path = getPathFromUrl(url);

  return (
    path === "/school/register" ||
    path === "/school/roster-template" ||
    path === "/school/roster-upload" ||
    path === "/health" ||
    path === "/docs" ||
    path === "/openapi.json"
  );
}

/**
 * Pick the best token for the target URL.
 * Priority:
 * 1) Route-specific token family
 * 2) Role-specific token family
 * 3) Generic fallback
 */
function bestTokenForUrl(url?: string): { token: string; source: string } {
  const path = getPathFromUrl(url);

  const adminKeys = [
    "admin_jwt",
    "admin_access_token",
    "admin_token",
    "access_token",
    "token",
  ];

  const doctorKeys = [
    "doctor_jwt",
    "doctor_access_token",
    "doctor_token",
    "access_token",
    "token",
  ];

  const studentKeys = [
    "student_jwt",
    "student_access_token",
    "student_token",
    "access_token",
    "token",
  ];

  const schoolKeys = [
    "school_jwt",
    "school_access_token",
    "school_token",
    "access_token",
    "token",
  ];

  const ptKeys = [
    "pt_jwt",
    "pt_access_token",
    "pt_token",
    "parent_teacher_token",
    "access_token",
    "token",
  ];

  const anyKeys = [
    "admin_jwt",
    "admin_access_token",
    "admin_token",
    "doctor_jwt",
    "doctor_access_token",
    "doctor_token",
    "student_jwt",
    "student_access_token",
    "student_token",
    "school_jwt",
    "school_access_token",
    "school_token",
    "pt_jwt",
    "pt_access_token",
    "pt_token",
    "parent_teacher_token",
    "access_token",
    "token",
  ];

  if (path.startsWith("/admin")) {
    const t = normalizeBearer(getFirst(adminKeys));
    if (t) return { token: t, source: "adminKeys:url" };
  }

  if (path.startsWith("/doctor")) {
    const t = normalizeBearer(getFirst(doctorKeys));
    if (t) return { token: t, source: "doctorKeys:url" };
  }

  if (path.startsWith("/student")) {
    const t = normalizeBearer(getFirst(studentKeys));
    if (t) return { token: t, source: "studentKeys:url" };
  }

  if (path.startsWith("/school")) {
    const t = normalizeBearer(getFirst(schoolKeys));
    if (t) return { token: t, source: "schoolKeys:url" };
  }

  if (
    path.startsWith("/pt") ||
    path.startsWith("/parent-teacher") ||
    path.startsWith("/parent_teacher")
  ) {
    const t = normalizeBearer(getFirst(ptKeys));
    if (t) return { token: t, source: "ptKeys:url" };
  }

  const role = getRole();

  if (role === "admin") {
    const t = normalizeBearer(getFirst(adminKeys));
    if (t) return { token: t, source: "adminKeys:role" };
  }

  if (role === "doctor") {
    const t = normalizeBearer(getFirst(doctorKeys));
    if (t) return { token: t, source: "doctorKeys:role" };
  }

  if (role === "student") {
    const t = normalizeBearer(getFirst(studentKeys));
    if (t) return { token: t, source: "studentKeys:role" };
  }

  if (role === "school" || role === "school_admin") {
    const t = normalizeBearer(getFirst(schoolKeys));
    if (t) return { token: t, source: "schoolKeys:role" };
  }

  if (role === "pt" || role === "parent_teacher" || role === "parent/teacher") {
    const t = normalizeBearer(getFirst(ptKeys));
    if (t) return { token: t, source: "ptKeys:role" };
  }

  const t = normalizeBearer(getFirst(anyKeys));
  if (t) return { token: t, source: "anyKeys" };

  return { token: "", source: "none" };
}

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
});

function isFormData(data: unknown): boolean {
  return typeof FormData !== "undefined" && data instanceof FormData;
}

function ensureHeaders(config: InternalAxiosRequestConfig) {
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  } else if (!(config.headers instanceof AxiosHeaders)) {
    config.headers = AxiosHeaders.from(config.headers);
  }
  return config.headers;
}

api.interceptors.request.use((config) => {
  const url = config.url || "";
  const skipAuth = Boolean((config as any).skipAuth) || isPublicEndpoint(url);

  const headers = ensureHeaders(config);

  if (skipAuth) {
    headers.delete("Authorization");
    headers.delete("authorization");
  } else {
    const pick = bestTokenForUrl(url);
    const cleanToken = normalizeBearer(pick.token);

    if (cleanToken) {
      headers.set("Authorization", `Bearer ${cleanToken}`);
    } else {
      headers.delete("Authorization");
      headers.delete("authorization");
    }

    try {
      const dbg = (window as any).__API_DEBUG_AUTH__;
      if (dbg) {
        console.log("[api] request", String(config.method || "get").toUpperCase(), url, {
          auth: headers.get("Authorization") ? "Bearer (present)" : "none",
          source: pick.source,
          role: getRole() || "(none)",
        });
      }
    } catch {}
  }

  const data = config.data;

  if (isFormData(data)) {
    headers.delete("Content-Type");
    headers.delete("content-type");
  } else {
    const method = String(config.method || "get").toLowerCase();
    const hasBody = ["post", "put", "patch"].includes(method) && data !== undefined;
    if (hasBody && !headers.get("Content-Type") && !headers.get("content-type")) {
      headers.set("Content-Type", "application/json");
    }
  }

  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => Promise.reject(err)
);

export default api;